import type { MarcaCliente } from './tipos';

/**
 * El catálogo del levantamiento de Yichang: los números y los textos de cada pregunta, las opciones
 * guardadas como identificador y qué parte de la ficha es de cada marca.
 *
 * Sale **del código de los dos formularios publicados**, no de la memoria:
 * `entregables/yichang-2026/app-moderno/index.html` (el objeto `GONDOLAS` y `fichaVacia`) y
 * `app-tradicional/index.html` (`CFG`, `fichaVacia`, `fichaMerVacia` y lo que agregan
 * `armaFichaBod` y `enviarMer`). Los números son los del cuestionario «para revisar», que es el que
 * lee el cliente: con ellos una respuesta de la bandeja se cruza con su pregunta en papel.
 *
 * ⚠️ **Este catálogo sirve para ROTULAR, no para filtrar.** Una llave que no está acá se muestra
 * igual, con un rótulo armado de su nombre (`respuestas.ts`): si el formulario agrega una pregunta,
 * la bandeja no la pierde. Lo que falta acá se ve peor, no desaparece.
 */

/** `[número del cuestionario, texto de la pregunta]`. */
export type Pregunta = readonly [string, string];

// ═══════════════════════════════════════════════════════════════════════════
// Canal moderno
// ═══════════════════════════════════════════════════════════════════════════

export interface SkuNucleo { k: string; nom: string; gr: string | null }

export interface GondolaYi {
  /** La llave de la sección en la ficha. */
  id: 'snacks' | 'cereales';
  marca: MarcaCliente;
  nom: string;
  marcaNom: string;
  letra: 'A' | 'B';
  nucleo: readonly SkuNucleo[];
  /** El choque obligatorio: el SKU propio contra su rival, con el gramaje por defecto del rival. */
  choque: { sku: string; propio: string; rival: string; grRival: string | null };
  /** 2.31 / 3.33 · los pares de choque adicional: contra qué SKU propio y con qué gramaje del rival. */
  choquesExtra: readonly { par: string; sku: string; rival: string; gr: string | null }[];
  /** 2.8 · frentes de lata. Solo snacks. `[llave, rótulo]`. */
  frentes: readonly (readonly [string, string])[] | null;
  /** Las listas de rivales (2.13 · 3.14 y 3.15): llave en la ficha y rótulo. */
  rivales: readonly (readonly [string, string])[];
  preguntas: Record<string, Pregunta>;
  /** Los números de cada campo de un precio (2.24 a 2.29 · 3.26 a 3.31). */
  numPrecio: { gramaje: string; regular: string; oferta: string; tarjeta: string; volumen: string; fleje: string };
  numPieza: { pieza: string; marca: string; fotos: string };
  numCartel: { marca: string; ofrece: string; tarjeta: string; fotos: string };
}

const PREGUNTAS_SNACKS: Record<string, Pregunta> = {
  m1_se_pudo_medir: ['2.1', '¿Pudiste medir esta sección?'],
  m2_donde_vive_pringles: ['2.2', '¿Dónde encontraste Pringles?'],
  m3_fotos_gondola: ['2.3', 'Fotos de la góndola completa'],
  m3_modulos: ['2.4', '¿Cuántos módulos tiene?'],
  m3_repisas: ['2.5', '¿Cuántas repisas tiene cada módulo?'],
  m3_carro_reposicion: ['2.6', '¿Hay carro de reposición en el pasillo?'],
  m4_espacio: ['2.7', '¿Cuánto espacio ocupa cada marca?'],
  m4_frentes_latas: ['2.8', 'En el tramo de latas, ¿cuántos frentes tiene cada marca?'],
  m4_frentes_estado: ['2.8', '¿Hay tramo de latas?'],
  m5_estado_sku: ['2.9', '¿Cómo está cada SKU núcleo?'],
  m13_almacen_segun_personal: ['2.10', '¿Hay de este producto en almacén? · según el personal'],
  m6_otras_presentaciones: ['2.11', 'Pringles · otras presentaciones que se vieron'],
  m6_otros_propios_texto: ['2.12', '¿Viste algún producto de Pringles que no esté en la lista?'],
  m6_rival_kryzpo: ['2.13', 'Kryzpo · el rival directo: lo que se vio'],
  m6_demas_marcas: ['2.14', 'Las demás marcas que están'],
  m6_otra_competencia_texto: ['2.15', '¿Viste alguna marca de la competencia que no esté en la lista?'],
  m6_revision_listas: ['2.16', '¿Revisaste completas las listas de 2.11, 2.13 y 2.14?'],
  m8_sin_material: ['2.17', 'Material publicitario'],
  m8_piezas: ['2.17', 'Material publicitario en esta góndola'],
  m9_sin_carteles: ['2.20', 'Promoción'],
  m9_carteles: ['2.20', 'Carteles de promoción'],
  m10_precios: ['2.24', 'Precio de cada SKU núcleo'],
  m10_choque_obligatorio: ['2.30', 'Choque obligatorio: Pringles 104 g Original contra Kryzpo 130 g'],
  m10_choques_adicionales_estado: ['2.31', 'Choques adicionales'],
  m10_choques_adicionales: ['2.31', 'Choques adicionales'],
  m10_otros_precios: ['2.32', 'Otros precios que se anotaron'],
  m11_impulsadora: ['2.33', '¿Hay impulsadora o degustación en este pasillo?'],
  m11_competencia_marca: ['2.34', '¿De qué marca es la de la competencia?'],
  m11_competencia_otra: ['2.34', 'La otra marca, escrita'],
  m14_observacion: ['2.35', 'Algo que un número no cuente'],
};

const PREGUNTAS_CEREALES: Record<string, Pregunta> = {
  m1_se_pudo_medir: ['3.1', '¿Pudiste medir esta sección?'],
  m3_fotos_gondola: ['3.2', 'Fotos de la góndola completa'],
  m3_modulos: ['3.3', '¿Cuántos módulos tiene?'],
  m3_repisas: ['3.4', '¿Cuántas repisas tiene cada módulo?'],
  m3_carro_reposicion: ['3.5', '¿Hay carro de reposición en el pasillo?'],
  m4_espacio: ['3.6', '¿Cuánto espacio ocupa cada marca?'],
  m7_bloque_ninos_repisas: ['3.7', "¿En qué repisas está el bloque de niños de Kellogg's?"],
  m7_corn_flakes_repisas: ['3.8', "¿Y en qué repisas está Corn Flakes de Kellogg's?"],
  m7_bloque_kelloggs: ['3.9', "¿El bloque de Kellogg's está junto o partido?"],
  m5_estado_sku: ['3.10', '¿Cómo está cada SKU núcleo?'],
  m13_almacen_segun_personal: ['3.11', '¿Hay de este producto en almacén? · según el personal'],
  m6_otras_presentaciones: ['3.12', "Kellogg's · otras líneas y presentaciones que se vieron"],
  m6_otros_propios_texto: ['3.13', "¿Viste algún producto de Kellogg's que no esté en la lista?"],
  m6_rival_1_angel: ['3.14', 'Ángel · rival 1: lo que se vio'],
  m6_rival_2_nestle: ['3.15', 'Nestlé · rival 2: lo que se vio'],
  m6_demas_marcas: ['3.16', 'Las demás marcas que están'],
  m6_otra_competencia_texto: ['3.17', '¿Viste alguna marca de la competencia que no esté en la lista?'],
  m6_revision_listas: ['3.18', '¿Revisaste completas las listas de 3.12, 3.14, 3.15 y 3.16?'],
  m8_sin_material: ['3.19', 'Material publicitario'],
  m8_piezas: ['3.19', 'Material publicitario en esta góndola'],
  m9_sin_carteles: ['3.22', 'Promoción'],
  m9_carteles: ['3.22', 'Carteles de promoción'],
  m10_precios: ['3.26', 'Precio de cada SKU núcleo'],
  m10_choque_obligatorio: ['3.32', 'Choque obligatorio: Zucaritas 420 g contra Ángel Zuck o Zucks'],
  m10_choques_adicionales_estado: ['3.33', 'Choques adicionales'],
  m10_choques_adicionales: ['3.33', 'Choques adicionales'],
  m10_otros_precios: ['3.34', 'Otros precios que se anotaron'],
  m11_impulsadora: ['3.35', '¿Hay impulsadora o degustación en este pasillo?'],
  m11_competencia_marca: ['3.36', '¿De qué marca es la de la competencia?'],
  m11_competencia_otra: ['3.36', 'La otra marca, escrita'],
  m14_observacion: ['3.37', 'Algo que un número no cuente'],
};

export const GONDOLAS_YI: readonly GondolaYi[] = [
  {
    id: 'snacks', marca: 'pringles', nom: 'Snacks salados', marcaNom: 'Pringles', letra: 'A',
    nucleo: [
      { k: 'pringles_19_21g', nom: 'Pringles 19-21 g', gr: null },
      { k: 'pringles_37g_original', nom: 'Pringles 37 g · Original', gr: '37 g' },
      { k: 'pringles_104g_original', nom: 'Pringles 104 g · Original', gr: '104 g' },
      { k: 'pringles_109g_queso', nom: 'Pringles 109 g · Queso', gr: '109 g' },
      { k: 'pringles_109g_crema_cebolla', nom: 'Pringles 109 g · Crema y Cebolla', gr: '109 g' },
      { k: 'pringles_158g_bbq', nom: 'Pringles 158 g · BBQ', gr: '158 g' },
    ],
    choque: { sku: 'pringles_104g_original', propio: 'Pringles 104 g Original', rival: 'Kryzpo 130 g', grRival: '130 g' },
    choquesExtra: [
      { par: "Pringles 104 g · lata Bell's 150 g", sku: 'pringles_104g_original', rival: "Lata Bell's 150 g", gr: '150 g' },
      { par: "Pringles 104 g · Lay's Clásicas 140 g", sku: 'pringles_104g_original', rival: "Lay's Clásicas 140 g", gr: '140 g' },
      { par: 'Pringles 37 g · Kryzpo 37 g', sku: 'pringles_37g_original', rival: 'Kryzpo 37 g', gr: '37 g' },
      { par: "Pringles 37 g · Lay's 36 g", sku: 'pringles_37g_original', rival: "Lay's 36 g", gr: '36 g' },
      { par: "Pringles 19-21 g · Lay's 36 g", sku: 'pringles_19_21g', rival: "Lay's 36 g", gr: '36 g' },
      { par: 'Pringles 19-21 g · Piqueo Snax 55 g', sku: 'pringles_19_21g', rival: 'Piqueo Snax 55 g', gr: '55 g' },
    ],
    frentes: [
      ['pringles', 'Pringles'], ['kryzpo', 'Kryzpo'],
      ['lata_marca_super', "Lata de la marca del súper (Bell's u otra)"], ['tiyapuy', 'Tiyapuy'],
    ],
    rivales: [['m6_rival_kryzpo', 'Kryzpo']],
    preguntas: PREGUNTAS_SNACKS,
    numPrecio: { gramaje: '2.24', regular: '2.25', oferta: '2.26', tarjeta: '2.27', volumen: '2.28', fleje: '2.29' },
    numPieza: { pieza: '2.17', marca: '2.18', fotos: '2.19' },
    numCartel: { marca: '2.20', ofrece: '2.21', tarjeta: '2.22', fotos: '2.23' },
  },
  {
    id: 'cereales', marca: 'kelloggs', nom: 'Cereales', marcaNom: "Kellogg's", letra: 'B',
    nucleo: [
      { k: 'zucaritas_420g', nom: 'Zucaritas 420 g', gr: '420 g' },
      { k: 'froot_loops_350g', nom: 'Froot Loops 350 g', gr: '350 g' },
      { k: 'corn_flakes_410g', nom: 'Corn Flakes 410 g', gr: '410 g' },
      { k: 'choco_krispis_420g', nom: 'Choco Krispis 420 g', gr: '420 g' },
      { k: 'musli_275g', nom: 'Müsli 275 g', gr: '275 g' },
    ],
    choque: { sku: 'zucaritas_420g', propio: 'Zucaritas 420 g', rival: 'Ángel Zuck o Zucks', grRival: null },
    choquesExtra: [
      { par: 'Zucaritas 420 g · Nestlé Zucosos', sku: 'zucaritas_420g', rival: 'Nestlé Zucosos', gr: null },
      { par: 'Corn Flakes 410 g · Ángel Flakes', sku: 'corn_flakes_410g', rival: 'Ángel Flakes', gr: null },
      { par: 'Corn Flakes 410 g · Nestlé Corn Flakes', sku: 'corn_flakes_410g', rival: 'Nestlé Corn Flakes', gr: null },
      { par: "Corn Flakes 410 g · Bell's Corn Flakes", sku: 'corn_flakes_410g', rival: "Bell's Corn Flakes", gr: null },
      { par: 'Froot Loops 350 g · Ángel Frutt', sku: 'froot_loops_350g', rival: 'Ángel Frutt', gr: null },
      { par: 'Froot Loops 350 g · Nestlé Trix', sku: 'froot_loops_350g', rival: 'Nestlé Trix', gr: null },
      { par: "Froot Loops 350 g · Bell's Aritos Frutados", sku: 'froot_loops_350g', rival: "Bell's Aritos Frutados", gr: null },
      { par: 'Choco Krispis 420 g · Ángel Chock', sku: 'choco_krispis_420g', rival: 'Ángel Chock', gr: null },
      { par: 'Choco Krispis 420 g · Nestlé Chocapic', sku: 'choco_krispis_420g', rival: 'Nestlé Chocapic', gr: null },
      { par: "Choco Krispis 420 g · Bell's Bolitas", sku: 'choco_krispis_420g', rival: "Bell's Bolitas", gr: null },
    ],
    frentes: null,
    rivales: [['m6_rival_1_angel', 'Ángel'], ['m6_rival_2_nestle', 'Nestlé']],
    preguntas: PREGUNTAS_CEREALES,
    numPrecio: { gramaje: '3.26', regular: '3.27', oferta: '3.28', tarjeta: '3.29', volumen: '3.30', fleje: '3.31' },
    numPieza: { pieza: '3.19', marca: '3.20', fotos: '3.21' },
    numCartel: { marca: '3.22', ofrece: '3.23', tarjeta: '3.24', fotos: '3.25' },
  },
];

export const GONDOLA_DE_MARCA: Record<MarcaCliente, GondolaYi> = {
  pringles: GONDOLAS_YI[0],
  kelloggs: GONDOLAS_YI[1],
};

/** 2.1 / 3.1. «Completa» y «A medias» abren la góndola; las otras tres la cierran. */
export const GATE_MEDIDA: readonly string[] = ['Completa', 'A medias'];
export const GATE_CERRADA: readonly string[] = ['No existe en esta tienda', 'En reposición', 'No me dejaron'];
export const GATE: readonly string[] = [...GATE_MEDIDA, ...GATE_CERRADA];

/** 2.9 / 3.10, en el orden del formulario. */
export const ESTADO_SKU: readonly string[] = [
  'Está', 'Cenefa sin producto', 'Ni cenefa ni producto', 'Solo en exhibición', 'No lo pude revisar',
];
/** Los estados del SKU que dicen, sin lugar a duda, que el producto NO está hoy. */
export const SKU_NO_ESTA: readonly string[] = ['Cenefa sin producto', 'Ni cenefa ni producto'];
/** Los que dicen que SÍ está en la tienda, en la góndola o fuera de ella. */
export const SKU_ESTA: readonly string[] = ['Está', 'Solo en exhibición'];

/** 2.30 / 3.32 · el rival del choque obligatorio. */
export const ESTADO_RIVAL: readonly string[] = [
  'Sí, está', 'Cenefa sin producto', 'Ni cenefa ni producto', 'No lo pude revisar',
];

/** Las opciones de 1.1 que dicen de qué marca fue la publicidad del camino. */
export const CAMINO_DE_MARCA: Record<MarcaCliente, string> = {
  pringles: 'Sí, de Pringles',
  kelloggs: "Sí, de Kellogg's",
};
export const CAMINO_NADA = 'No vi nada';

/** Las otras cuatro secciones de la ficha de moderno, con sus preguntas. */
export const PREGUNTAS_ENTRADA_MOD: Record<string, Pregunta> = {
  supervisor: ['0.1', 'Supervisor'],
  cadena: ['0.2', 'Cadena'],
  cadena_elegida: ['0.2', 'Cadena elegida en la lista'],
  tienda: ['0.3', 'Tienda'],
  tienda_de_la_lista: ['0.3', '¿La tienda salió de la lista?'],
  formato: ['0.4', 'Formato de tienda'],
  formato_en_la_lista: ['0.4', 'Formato que dice la lista de tiendas'],
};
export const PREGUNTAS_CAMINO: Record<string, Pregunta> = {
  m0_publicidad_camino: ['1.1', "Desde que entraste hasta llegar al pasillo, ¿viste publicidad de Pringles o de Kellogg's?"],
  m0_publicidad_detalle: ['1.2', 'Describe lo que viste'],
};
export const PREGUNTAS_VUELTA: Record<string, Pregunta> = {
  c1_exhibiciones: ['4.1', 'Exhibiciones fuera del pasillo'],
  c1_como_termino_vuelta: ['4.6', '¿Cómo terminó la vuelta?'],
  c2_cajas_abiertas: ['4.7', '¿Cuántas cajas hay abiertas ahora?'],
  c2_cajas_con_pringles: ['4.8', '¿En cuántas de esas se ve Pringles?'],
  c2_formato_pringles_caja: ['4.9', '¿En qué formato?'],
  c2_cajas_con_otra_papa: ['4.10', '¿En cuántas se ve otra papa?'],
  c2_marcas_otra_papa: ['4.11', '¿De qué marca?'],
  c2_marca_otra_texto: ['4.11', 'La otra marca, escrita'],
  c2_foto_caja: ['4.12', 'Foto de una caja con Pringles'],
};
/** Las llaves de la vuelta que son de la zona de cajas: toda la zona de cajas es de Pringles. */
export const LLAVES_CAJAS: readonly string[] = [
  'c2_cajas_abiertas', 'c2_cajas_con_pringles', 'c2_formato_pringles_caja', 'c2_cajas_con_otra_papa',
  'c2_marcas_otra_papa', 'c2_marca_otra_texto', 'c2_foto_caja',
];
export const PREGUNTAS_CIERRE_MOD: Record<string, Pregunta> = {
  c3_gondolas_medidas: ['5.1', '¿Pudiste medir las dos góndolas?'],
  c3_motivo_sin_foto: ['5.2', 'Si alguna foto no se pudo tomar, ¿por qué?'],
  c3_motivo_sin_foto_otro: ['5.2', 'El otro motivo, escrito'],
  c3_observacion: ['5.3', 'Algo más que quieras dejar dicho'],
};
/** Los campos de una exhibición de la vuelta (4.1 a 4.5). */
export const CAMPOS_EXHIBICION: Record<string, Pregunta> = {
  tipo: ['4.1', '¿Qué tipo de exhibición es?'],
  ubic: ['4.2', '¿Dónde estaba?'],
  marca: ['4.3', '¿De qué marca es la exhibición?'],
  marca_otra: ['4.3', 'La otra marca, escrita'],
  lleno: ['4.4', '¿Cuánto producto tenía?'],
  fotos: ['4.5', 'Foto de la exhibición'],
};

/** Las secciones de la ficha de moderno, en el orden del recorrido. */
export const SECCIONES_MODERNO: readonly { id: string; titulo: string }[] = [
  { id: 'entrada', titulo: 'Antes de entrar' },
  { id: 'camino', titulo: 'Camino al pasillo' },
  { id: 'snacks', titulo: 'Góndola A · snacks salados · Pringles' },
  { id: 'cereales', titulo: "Góndola B · cereales · Kellogg's" },
  { id: 'vuelta', titulo: 'Vuelta por la tienda' },
  { id: 'cierre', titulo: 'Cerrar la visita' },
];

/** Llaves que el formulario escribe como metadato y que no son una respuesta. */
export const META_MODERNO: readonly string[] = ['version', 'formulario', 'nombre', 'marca_cliente'];

// ═══════════════════════════════════════════════════════════════════════════
// Canal tradicional: bodegas, mercados y cierre de jornada
// ═══════════════════════════════════════════════════════════════════════════

export const PREGUNTAS_BODEGA: Record<string, Pregunta> = {
  e1_supervisor: ['E.1', 'Supervisor'],
  e1_supervisor_fuera_de_lista: ['E.1', '¿El nombre se escribió fuera de la lista?'],
  e2_zona: ['E.2', 'Zona de Lima'],
  e3_distrito: ['E.3', 'Distrito'],
  e4_tipo_bodega: ['E.4', 'Tipo de bodega'],
  e5_modo_visita: ['E.5', '¿Cómo entraste a esta bodega?'],
  e6_nombre_bodega: ['E.6', 'Nombre de la bodega'],
  e7_direccion: ['E.7', 'Dirección o referencia'],
  e8_es_cliente_yichang: ['E.8', '¿Es cliente de Yichang?'],
  e9_codigo_cliente_yichang: ['E.9', 'Código de cliente de Yichang'],
  e10_contacto: ['E.10', '¿Aceptó dar su contacto?'],
  t0_1_vende_snacks: ['T0.1', '¿Vende snacks salados?'],
  t0_2_vende_cereal: ['T0.2', '¿Vende cereal?'],
  t2_1_fotos_fachada: ['T2.1', 'Foto de la fachada completa'],
  t2_2_letrero: ['T2.2', 'Letrero de la bodega'],
  t2_3_pintada: ['T2.3', 'Fachada pintada'],
  t2_4_toldo: ['T2.4', 'Toldo'],
  t2_5_reja: ['T2.5', 'Reja o cortina'],
  t2_6_viniles: ['T2.6', 'Viniles o stickers'],
  t2_7_afiches: ['T2.7', 'Afiches pegados afuera'],
  t2_8_jalavistas: ['T2.8', 'Jalavistas, flyers o volantes'],
  t2_9_se_nota_mas: ['T2.9', 'De esas, ¿cuál se nota más desde la vereda?'],
  t2_10_obs_exterior: ['T2.10', 'Algo más del exterior'],
  t3_elementos: ['T3.1', 'Elementos de exhibición'],
  t3_5_ningun_elemento: ['T3.5', '¿No había ninguno?'],
  t3_6_marca_exhibidor_snacks: ['T3.6', '¿De qué marca es el exhibidor donde están los snacks?'],
  t3_7_donde_esta_pringles: ['T3.7', '¿Dónde está Pringles?'],
  t3_8_fotos_mostrador_snacks: ['T3.8', 'Foto del mostrador o exhibidor de snacks'],
  t3_9_fotos_cereal: ['T3.9', 'Foto de donde está el cereal'],
  t3_10_obs_exhibicion: ['T3.10', 'Observación de exhibición'],
  t4_1_snacks_vistos: ['T4.1', '¿Qué marcas de snacks se ven?'],
  t4_2_formatos_snacks: ['T4.2', '¿En qué formatos se ven los snacks, de cualquier marca?'],
  t4_3_snack_se_ve_mas: ['T4.3', '¿Qué marca de snacks se ve más?'],
  t4_4_cereales_vistos: ['T4.4', '¿Qué cereales se ven?'],
  t4_5_presentacion_kelloggs: ['T4.5', "¿En qué presentación se ve cada línea de Kellogg's?"],
  t4_6_formatos_cereal: ['T4.6', '¿En qué formatos se ve el cereal, de cualquier marca?'],
  t4_7_cereal_se_ve_mas: ['T4.7', '¿Qué marca de cereal se ve más?'],
  t4_8_avena: ['T4.8', '¿Se ve avena?'],
  t4b_1_pringles_chica: ['T4b.1', '«¿Tiene Pringles chiquita?» · lata de 19-21 g'],
  t4b_2_pringles_mediana: ['T4b.2', '«¿Tiene Pringles mediana?» · lata de 37-40 g'],
  t4b_3_zucaritas: ['T4b.3', '«¿Tiene Zucaritas?»'],
  t4b_4_corn_flakes: ['T4b.4', "«¿Tiene Corn Flakes, el de Kellogg's?»"],
  t4b_5_froot_loops: ['T4b.5', '«¿Tiene Froot Loops?»'],
  t4b_6_choco_krispis: ['T4b.6', '«¿Tiene Choco Krispis?»'],
  t4c_1_por_que_no_pringles: ['T4c.1', '¿Por qué no trabaja Pringles? · declarado'],
  t4c_1_otro: ['T4c.1', 'El otro motivo, escrito'],
  t4c_2_por_que_no_kelloggs: ['T4c.2', "¿Por qué no trabaja Kellogg's? · declarado"],
  t4c_2_otro: ['T4c.2', 'El otro motivo, escrito'],
  t5_choque_pringles: ['T5.6', "Choque obligatorio: Pringles chica contra Lay's 36 g, o Piqueo Snax 55 g si no hay Lay's"],
  t5_choque_kelloggs: ['T5.6', "Choque obligatorio: bolsa de Kellogg's contra bolsa de Ángel"],
  t5_choques_otros: ['T5.6', 'Otros choques de precio'],
  t5_precios: ['T5.1', 'Otros precios'],
  t5_8_cereal_mas_barato: ['T5.8', '¿Cuál es el cereal que cuesta menos plata en la bodega?'],
  t5_9_avena_mas_barata: ['T5.9', '¿Y la avena que cuesta menos plata?'],
  t5_promos: ['T5.10', 'Promociones a la vista'],
  t5_14_ninguna_promocion: ['T5.14', '¿No había ninguna promoción?'],
  t6_1_categoria: ['T6.1', '¿Por qué categoría le preguntaste?'],
  t6_2_recomienda_snacks: ['T6.2', '«¿Qué papitas me recomienda?» ¿Qué marca te recomendó?'],
  t6_2_otra: ['T6.2', 'La otra marca, escrita'],
  t6_3_recomienda_cereal: ['T6.3', '«¿Qué cereal le doy a mi hijo?» ¿Qué te recomendó?'],
  t6_3_otra: ['T6.3', 'La otra marca, escrita'],
  t6_4_como_respondio_snacks: ['T6.4', '¿Cómo respondió? · snacks'],
  t6_4_como_respondio_cereal: ['T6.4', '¿Cómo respondió? · cereal'],
  t7_1_donde_compra_pringles: ['T7.1', '«¿Dónde compra la Pringles?» · declarado'],
  t7_2_frecuencia_pringles: ['T7.2', '¿Cada cuánto compra Pringles? · declarado'],
  t7_3_pago_pringles: ['T7.3', '¿Cómo paga Pringles? · declarado'],
  t7_4_a_cuanto_pringles: ['T7.4', '¿A cuánto compra Pringles? · declarado'],
  t7_1_donde_compra_kelloggs: ['T7.1', "«¿Dónde compra el Kellogg's?» · declarado"],
  t7_2_frecuencia_kelloggs: ['T7.2', "¿Cada cuánto compra Kellogg's? · declarado"],
  t7_3_pago_kelloggs: ['T7.3', "¿Cómo paga Kellogg's? · declarado"],
  t7_4_a_cuanto_kelloggs: ['T7.4', "¿A cuánto compra Kellogg's? · declarado"],
  t10_1_fuera_de_lista: ['T10.1', '¿Viste alguna marca, presentación o exhibidor fuera de la lista?'],
  t10_1_nada_fuera: ['T10.1', 'Nada fuera de la lista'],
  t10_2_motivos: ['T10.2', '¿Por qué falta cada foto obligatoria?'],
  t10_2_otro: ['T10.2', 'El otro motivo, escrito'],
  t10_3_obs: ['T10.3', 'Observación libre'],
};

export const PREGUNTAS_MERCADO: Record<string, Pregunta> = {
  me1_supervisor: ['mE.1', 'Supervisor'],
  me1_supervisor_fuera_de_lista: ['mE.1', '¿El nombre se escribió fuera de la lista?'],
  me2_mercado: ['mE.2', 'Mercado'],
  mercado_escrito_a_mano: ['mE.3', '¿El mercado se escribió a mano?'],
  me4_zona: ['mE.4', 'Zona de Lima'],
  me5_distrito: ['mE.5', 'Distrito'],
  me6_direccion: ['mE.6', 'Dirección o referencia'],
  m1_1_numero: ['m1.1', 'Número del puesto en el recorrido'],
  m1_2_nombre: ['m1.2', 'Nombre o seña del puesto'],
  m1_3_levantado: ['m1.3', '¿Pudiste levantar este puesto?'],
  m1_4_motivo_no: ['m1.4', '¿Por qué no?'],
  m1_4_otro: ['m1.4', 'Qué pasó, escrito'],
  m2_1_rubro: ['m2.1', '¿De qué rubro es el puesto?'],
  m2_2_categorias: ['m2.2', '¿Cuáles de estas categorías vende?'],
  m3_1_a_quien_vende: ['m3.1', '¿A quién le vende este puesto? · declarado'],
  m4_1_estado_por_marca: ['m4.1', 'Marca por marca: ¿cómo está hoy?'],
  m4_2_pringles_tamanos: ['m4.2', 'Pringles: ¿en qué tamaño la tiene?'],
  m4_3_cereal_a_granel: ['m4.3', '¿Vende cereal a granel, por kilo?'],
  m5_precios: ['m5.1', 'Precios y presentaciones'],
  m5_8_avena_mas_barata: ['m5.8', '¿Cuál es la avena que cuesta menos plata en el puesto?'],
  m6_promos: ['m6.1', 'Promociones a la vista'],
  m6_6_ninguna_promocion: ['m6.6', '¿No había ninguna promoción?'],
  m7_1_donde_saca: ['m7.1', '¿De dónde saca el producto? · declarado'],
  m7_2_frecuencia: ['m7.2', '¿Cada cuánto compra? · declarado'],
  m7_3_distribuidor: ['m7.3', '¿Cómo se llama el distribuidor o bróker?'],
  m7_4_visita_vendedor: ['m7.4', '¿Lo visita algún vendedor o impulsador de marca?'],
  m7_5_de_que_marca: ['m7.5', '¿De qué marca o empresa?'],
  m8_1_ofrece_snacks: ['m8.1', 'Si entra un cliente y no pide marca, ¿cuál le ofrece primero? · snacks'],
  m8_1_otra_snacks: ['m8.1', 'La otra marca, escrita · snacks'],
  m8_2_por_que_snacks: ['m8.2', '¿Por qué esa? · snacks · declarado'],
  m8_1_ofrece_cereales: ['m8.1', 'Si entra un cliente y no pide marca, ¿cuál le ofrece primero? · cereal'],
  m8_1_otra_cereales: ['m8.1', 'La otra marca, escrita · cereal'],
  m8_2_por_que_cereales: ['m8.2', '¿Por qué esa? · cereal · declarado'],
  m9_1_fotos_puesto: ['m9.1', 'Foto del puesto'],
  m9_2_fuera_de_lista: ['m9.2', 'Marcas que viste y no estaban en la lista'],
  m9_2_ninguna: ['m9.2', 'Ninguna fuera de la lista'],
  m9_3_obs: ['m9.3', 'Algo más de este puesto'],
};

export const PREGUNTAS_CIERRE_MER: Record<string, Pregunta> = {
  j1_afluencia: ['J.1', '¿Cómo estuvo la afluencia hoy?'],
  j2_actividad_adicional: ['J.2', '¿Hoy había alguna actividad adicional?'],
  j3_que_paso: ['J.3', '¿Pasó algo que explique el día?'],
  j4_competencia: ['J.4', 'Competencia en el mercado'],
  j5_obs: ['J.5', 'Algo más que quieras dejar dicho'],
  me1_supervisor: ['mE.1', 'Supervisor'],
  me2_mercado: ['mE.2', 'Mercado'],
  mercado_escrito_a_mano: ['mE.3', '¿El mercado se escribió a mano?'],
};

/** Los campos de un precio del tradicional (`precioVacio` más lo que agrega `recorrePrecios`). */
export const CAMPOS_PRECIO_TRAD: Record<string, string> = {
  m5_1_categoria: 'Categoría',
  producto: 'Producto o marca',
  producto_otro: '¿Cuál?',
  variedad: 'Variedad',
  presentacion: 'Presentación',
  gramaje: 'Gramaje del empaque',
  gramaje_otro_g: 'Gramaje escrito (g)',
  unidades: '¿Cuántas unidades trae?',
  origen: '¿De dónde sale el precio?',
  precio_regular: 'Precio regular',
  precio_oferta: 'Precio de oferta',
  precio_tarjeta: 'Precio con tarjeta o app',
  falta: 'Si falta este lado, ¿qué pasó?',
  fotos: 'Foto del precio',
  precio_x100g: 'Por 100 g · calculado',
  precio_oferta_x100g: 'En oferta, por 100 g · calculado',
  precio_es_por_kilo: 'El precio es por kilo (a granel)',
  declarado: 'Lo dijo el comerciante',
  obligatorio: 'Precio obligatorio',
};

export const CAMPOS_ELEMENTO_T3: Record<string, Pregunta> = {
  t3_1_tipo: ['T3.1', 'Tipo de elemento'],
  t3_2_marca: ['T3.2', 'Marca del elemento'],
  t3_3_contenido: ['T3.3', '¿Qué producto tiene adentro?'],
  t3_4_fotos: ['T3.4', 'Foto del elemento'],
};
export const CAMPOS_PROMO_T5: Record<string, Pregunta> = {
  t5_10_marca: ['T5.10', 'Marca de la promoción'],
  t5_10_marca_otra: ['T5.10', 'La otra marca, escrita'],
  t5_11_mecanica: ['T5.11', 'Mecánica'],
  t5_12_texto: ['T5.12', 'Qué dice el cartel'],
  t5_13_fotos: ['T5.13', 'Foto de la promoción'],
};
export const CAMPOS_PROMO_M6: Record<string, Pregunta> = {
  m6_1_de_quien: ['m6.1', '¿De quién es?'],
  m6_2_marca: ['m6.2', 'Marca'],
  m6_2_marca_otra: ['m6.2', 'La otra marca, escrita'],
  m6_3_mecanica: ['m6.3', 'Mecánica'],
  m6_4_texto: ['m6.4', 'Qué dice el cartel'],
  m6_5_fotos: ['m6.5', 'Foto de la promoción'],
};

/** Llaves del tradicional que son configuración del formulario y no una respuesta. */
export const META_TRADICIONAL: readonly string[] = [
  'version', 'instrumento', 'pilares', 'no_puntuan', 'faltantes', 'fotos_obligatorias_faltantes',
];

/** Los bloques del tradicional, en el orden del recorrido. `prefijo` decide qué llaves caen en cada uno. */
export const BLOQUES_BODEGA: readonly { id: string; prefijo: RegExp; titulo: string }[] = [
  { id: 'e', prefijo: /^e\d+_/, titulo: 'Antes de entrar' },
  { id: 't0', prefijo: /^t0_/, titulo: 'T0 · Puerta de la bodega' },
  { id: 't2', prefijo: /^t2_/, titulo: 'T2 · Desde afuera' },
  { id: 't3', prefijo: /^t3_/, titulo: 'T3 · Al entrar: los muebles' },
  { id: 't4', prefijo: /^t4_/, titulo: 'T4 · Lo que se ve' },
  { id: 't4b', prefijo: /^t4b_/, titulo: 'T4b · Lo que no se ve' },
  { id: 't4c', prefijo: /^t4c_/, titulo: 'T4c · Por qué no la trabaja' },
  { id: 't5', prefijo: /^t5_/, titulo: 'T5 · Precio y promoción' },
  { id: 't6', prefijo: /^t6_/, titulo: 'T6 · El bodeguero · referencial' },
  { id: 't7', prefijo: /^t7_/, titulo: 'T7 · De dónde saca el producto' },
  { id: 't10', prefijo: /^t10_/, titulo: 'T10 · Cierre de la ficha' },
];
export const BLOQUES_MERCADO: readonly { id: string; prefijo: RegExp; titulo: string }[] = [
  { id: 'me', prefijo: /^(me\d+_|mercado_escrito_a_mano$)/, titulo: 'Antes de entrar al mercado' },
  { id: 'm1', prefijo: /^m1_/, titulo: 'm1 · ¿Qué puesto es?' },
  { id: 'm2', prefijo: /^m2_/, titulo: 'm2 · ¿Qué vende este puesto?' },
  { id: 'm3', prefijo: /^m3_/, titulo: 'm3 · ¿A quién le vende más?' },
  { id: 'm4', prefijo: /^m4_/, titulo: 'm4 · Marca por marca' },
  { id: 'm5', prefijo: /^m5_/, titulo: 'm5 · Precios y presentaciones' },
  { id: 'm6', prefijo: /^m6_/, titulo: 'm6 · Promociones a la vista' },
  { id: 'm7', prefijo: /^m7_/, titulo: 'm7 · De dónde saca el producto' },
  { id: 'm8', prefijo: /^m8_/, titulo: 'm8 · ¿Qué le ofrece al cliente? · referencial' },
  { id: 'm9', prefijo: /^m9_/, titulo: 'm9 · Foto y lo que no estaba' },
];

/** T4b: el producto núcleo que se pregunta en voz alta, de qué marca es y cómo se vio en T4. */
export const T4B: readonly { k: string; cod: string; nom: string; marca: MarcaCliente }[] = [
  { k: 't4b_1_pringles_chica', cod: 'T4b.1', nom: 'Pringles chica (19-21 g)', marca: 'pringles' },
  { k: 't4b_2_pringles_mediana', cod: 'T4b.2', nom: 'Pringles mediana (37-40 g)', marca: 'pringles' },
  { k: 't4b_3_zucaritas', cod: 'T4b.3', nom: 'Zucaritas', marca: 'kelloggs' },
  { k: 't4b_4_corn_flakes', cod: 'T4b.4', nom: "Corn Flakes de Kellogg's", marca: 'kelloggs' },
  { k: 't4b_5_froot_loops', cod: 'T4b.5', nom: 'Froot Loops', marca: 'kelloggs' },
  { k: 't4b_6_choco_krispis', cod: 'T4b.6', nom: 'Choco Krispis', marca: 'kelloggs' },
];
/** Las respuestas de T4b en el orden del formulario, más las dos que escribe `armaFichaBod`. */
export const T4B_OPS: readonly (readonly [string, string])[] = [
  ['se_vio_en_t4', 'Se vio a la vista (T4)'],
  ['la_saco', 'Sí, la sacó'],
  ['dice_tiene_declarado', 'Dice que tiene, pero no la sacó · declarado'],
  ['se_le_acabo', 'La trabaja, pero se le acabó'],
  ['no_la_trabaja', 'No la trabaja'],
  ['no_contesto', 'No contestó'],
  ['no_se_pudo_preguntar', 'No se pudo preguntar'],
  ['no_vende_la_categoria', 'No vende la categoría'],
];

/** Las líneas de Kellogg's tal como las nombran las listas del tradicional (T4.4 y m4.1). */
export const K_OTRA = "Otra de Kellogg's (Müsli, Krispis, Tres Chocolates…)";
export const LINEAS_KELLOGGS: readonly string[] = ['Zucaritas', 'Corn Flakes', 'Froot Loops', 'Choco Krispis', K_OTRA];

/** m4.1 · las marcas que se preguntan en el puesto, por categoría (`CFG.MER.MARCAS`). */
export const MARCAS_MERCADO: Record<'snacks' | 'cereales', readonly string[]> = {
  snacks: ['Pringles', "Lay's", 'Doritos', 'Cheetos', 'Piqueo Snax', 'Cheese Tris', 'Chizitos', 'Karinto',
    'Inka Chips', 'Kryzpo', 'Tiyapuy'],
  cereales: ['Zucaritas', 'Corn Flakes', 'Froot Loops', 'Choco Krispis', K_OTRA, 'Ángel', 'Nestlé', "D'Gussto",
    'Kiwigen · mismo distribuidor', 'Sol del Cusco · mismo distribuidor', 'Vitaplus', 'Forte Golpe',
    'Atómico', 'Bio Maca', 'La Molienda'],
};
/** m4.1 · los cuatro estados de una marca en el puesto. */
export const ESTADO_MERCADO: readonly (readonly [string, string])[] = [
  ['tiene', 'Tiene'], ['se_le_acabo', 'Se le acabó'], ['no_la_trabaja', 'No la trabaja'],
  ['no_se_pudo_saber', 'No se pudo saber'],
];

// ═══════════════════════════════════════════════════════════════════════════
// Opciones guardadas como identificador
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Cómo se lee cada identificador que guarda el tradicional. Los que dependen de la pregunta van en
 * `OPCIONES_POR_CLAVE`: `no_se_ve` quiere decir cosas distintas en T3.7 y en T4.8.
 */
export const OPCIONES: Record<string, string> = {
  si: 'Sí', no: 'No', no_sabe: 'No sabe', prefiere_no_decir: 'Prefiere no decir',
  no_se_pudo_preguntar: 'No se pudo preguntar', no_se_pudo_saber: 'No se pudo saber',
  no_se_pidio: 'No se le pidió', no_aplica_como_cliente: 'No aplica: entró como cliente',
  si_se_ven: 'Sí, se ven', si_se_ve: 'Sí, se ve', dice_que_tiene: 'No se ve, pero dice que tiene',
  no_vende: 'No vende',
  minimarket: 'Minimarket', grande: 'Bodega grande', mediana: 'Bodega mediana',
  mostrador: 'Bodega de mostrador', ventana: 'Bodega de ventana',
  cliente: 'Como cliente', identificado: 'Identificado como Treid', mixto: 'Mixto',
  lleno_su_marca: 'Lleno con su marca', a_medias_su_marca: 'A medias, con su marca',
  con_otra_marca: 'Con otra marca adentro', vacio: 'Vacío',
  exhibidor_pringles: 'En un exhibidor de Pringles', exhibidor_otra_marca: 'En el exhibidor de otra marca',
  sin_exhibidor: 'En el estante, el mostrador o colgada, sin exhibidor',
  no_lo_mire: 'No lo miré',
  visto: 'Lo vio: etiqueta, cartel o lista de precios', declarado: 'Se lo dijeron · declarado',
  no_lo_trabaja: 'No lo trabaja', no_quiso_decir_precio: 'No quiso decir el precio',
  snacks: 'Snacks', cereal: 'Cereal', cereales: 'Cereales', avena: 'Avena', nada: 'Nada de esto',
  las_dos: 'Las dos',
  obligatorio_pringles_chica: "Pringles chica contra Lay's 36 g o Piqueo Snax 55 g",
  obligatorio_bolsa_kelloggs: "Bolsa de Kellogg's contra bolsa de Ángel",
  opcional_pringles37_kryzpo37: 'Pringles 37 g contra Kryzpo 37 g',
  otro: 'Otro',
  contra_lays: "Contra Lay's 36 g", contra_piqueo: "Contra Piqueo Snax 55 g (no había Lay's)",
  zucaritas_vs_zuck: 'Zucaritas 90 g contra Ángel Zuck',
  froot_loops_vs_frutt: 'Froot Loops 75 g contra Ángel Frutt',
  corn_flakes_vs_flakes: 'Corn Flakes 80 g contra Ángel Flakes',
  no_hay: 'No hay', no_se: 'No sé',
  tiene: 'Tiene', si_azucarado: 'Sí, cereal azucarado o de colores',
  si_solo_hojuelas_o_avena: 'Sí, pero solo hojuelas o avena',
  ...Object.fromEntries(T4B_OPS),
};

const T0_SNACKS: Record<string, string> = {
  si_se_ven: 'Sí, se ven', dice_que_tiene: 'No se ven, pero dice que tiene', no_vende: 'No vende',
  no_se_pudo_saber: 'No se pudo saber',
};
const T0_CEREAL: Record<string, string> = {
  si_se_ve: 'Sí, se ve', dice_que_tiene: 'No se ve, pero dice que tiene', no_vende: 'No vende',
  no_se_pudo_saber: 'No se pudo saber',
};
const T4B_MAPA: Record<string, string> = Object.fromEntries(T4B_OPS);

export const OPCIONES_POR_CLAVE: Record<string, Record<string, string>> = {
  t0_1_vende_snacks: T0_SNACKS,
  t0_2_vende_cereal: T0_CEREAL,
  t3_7_donde_esta_pringles: {
    exhibidor_pringles: 'En un exhibidor de Pringles', exhibidor_otra_marca: 'En el exhibidor de otra marca',
    sin_exhibidor: 'En el estante, el mostrador o colgada, sin exhibidor',
    no_se_ve: 'No se ve Pringles (se pregunta en T4b)', no_vende_la_categoria: 'No vende la categoría',
  },
  t4_8_avena: { si_se_ve: 'Sí, se ve', no_se_ve: 'No se ve', no_lo_mire: 'No lo miré' },
  // ⚠️ `cliente` quiere decir TRES cosas en el tradicional: en E.5 es «entró como cliente», en m6.1 es
  // «la promoción es de Pringles o de Kellogg's». Sin este mapa, m6.1 decía «Como cliente».
  m6_1_de_quien: { cliente: "De Pringles o de Kellogg's", competencia: 'De la competencia' },
  // E.10: las opciones de `montaContacto`, que no son el «Sí / No» genérico.
  e10_contacto: {
    si: 'Sí, aceptó', no: 'No quiso', no_se_pidio: 'No se le pidió',
    no_aplica_como_cliente: 'No aplica: entró como cliente',
  },
  // m2.2: `CFG.MER.CATS`. En T6.1 «snacks» se lee «Snacks»; acá es «Snacks salados».
  m2_2_categorias: { snacks: 'Snacks salados', cereales: 'Cereales', avena: 'Avena', nada: 'Nada de esto' },
  m4_1_estado_por_marca: Object.fromEntries(ESTADO_MERCADO),
  ...Object.fromEntries(T4B.map((x) => [x.k, T4B_MAPA])),
};

// ═══════════════════════════════════════════════════════════════════════════
// Señales
// ═══════════════════════════════════════════════════════════════════════════

export const BANDERAS_YI: Record<string, { label: string; tono: 'aviso' | 'grave' | 'neutro' }> = {
  sin_gps: { label: 'sin GPS', tono: 'neutro' },
  ubicacion_de_la_entrada: { label: 'ubicación tomada al entrar', tono: 'neutro' },
  tienda_fuera_de_maestra: { label: 'tienda escrita a mano', tono: 'aviso' },
  cadena_escrita_a_mano: { label: 'cadena escrita a mano', tono: 'aviso' },
  muy_rapida: { label: 'muy rápida', tono: 'aviso' },
  sin_espacio_snacks: { label: 'snacks sin espacio pintado', tono: 'aviso' },
  sin_espacio_cereales: { label: 'cereales sin espacio pintado', tono: 'aviso' },
  fotos_sin_subir: { label: 'fotos que no subieron', tono: 'grave' },
  enviada_con_pendientes: { label: 'enviada con pendientes', tono: 'aviso' },
  sin_foto_fachada: { label: 'sin foto de la fachada', tono: 'aviso' },
  falta_foto_obligatoria: { label: 'falta una foto obligatoria', tono: 'aviso' },
  incompleta: { label: 'enviada incompleta', tono: 'aviso' },
  supervisor_fuera_de_lista: { label: 'supervisor fuera de la lista', tono: 'neutro' },
  no_levantado: { label: 'no se pudo levantar', tono: 'neutro' },
  no_trabaja_estas_categorias: { label: 'no trabaja estas categorías', tono: 'neutro' },
  sin_foto: { label: 'sin foto del puesto', tono: 'aviso' },
  mercado_escrito_a_mano: { label: 'mercado escrito a mano', tono: 'aviso' },
  demo: { label: 'ficha de práctica', tono: 'grave' },
};

// ═══════════════════════════════════════════════════════════════════════════
// De qué marca es cada cosa
// ═══════════════════════════════════════════════════════════════════════════

/**
 * El lado de la góndola al que pertenece una marca escrita: el de Pringles (snacks salados) o el de
 * Kellogg's (cereal y desayuno).
 *
 * Sirve para repartir las listas mezcladas —exhibiciones de la vuelta, elementos de T3,
 * promociones, precios sueltos— entre las dos marcas del cliente cuando se enfoca una. Una marca
 * que no es de ninguna de las dos categorías (Coca-Cola, cerveza, «Marca del súper», «Otra») da
 * `null` y queda en «lo demás de la visita»: repartirla a ojo sería afirmar algo que la ficha no
 * dice.
 *
 * ⚠️ La avena va del lado de Kellogg's a propósito: el formulario la pone en «lo más barato del
 * desayuno» junto al cereal (T5.8 y T5.9), y es la competencia de precio del cereal en la bodega.
 */
const SNACKS_RE = /pringles|lay'?s|pepsico|inka ?chips|kryzpo|kryspo|doritos|cheetos|piqueo|cheese ?tris|chizitos|karinto|tiyapuy|voraz|papa/i;
const CEREAL_RE = /kellogg|zucaritas|corn ?flakes|froot ?loops|krispis|tres chocolates|m[uü]sli|[aá]ngel|nestl[eé]|chocapic|trix|zucosos|zuck|d'?gussto|kiwigen|sol del cusco|vitaplus|forte golpe|at[oó]mico|bio ?maca|la molienda|avena|quaker|3 ositos|santa catalina|nature valley|cereal/i;

export function ladoDeMarca(texto: string | null | undefined): MarcaCliente | null {
  if (texto == null || texto.trim() === '') return null;
  const s = SNACKS_RE.test(texto);
  const c = CEREAL_RE.test(texto);
  if (s === c) return null;
  return s ? 'pringles' : 'kelloggs';
}

/**
 * De qué marca es una pregunta del tradicional, por su llave. `null` = de la visita, no de una
 * marca (la fachada, el rubro, el abasto del puesto, el cierre).
 */
const CLAVES_PRINGLES = /^(t0_1_|t3_[678]_|t4_[123]_|t4b_[12]_|t4c_1_|t5_choque_pringles$|t6_2_|t6_4_como_respondio_snacks$|t7_\d_\w+_pringles$|m4_2_|m8_\d_\w*snacks$)/;
const CLAVES_KELLOGGS = /^(t0_2_|t3_9_|t4_[45678]_|t4b_[3456]_|t4c_2_|t5_choque_kelloggs$|t5_[89]_|t6_3_|t6_4_como_respondio_cereal$|t7_\d_\w+_kelloggs$|m4_3_|m5_8_|m8_\d_\w*cereales$)/;

export function ladoDeClave(k: string): MarcaCliente | null {
  if (CLAVES_PRINGLES.test(k)) return 'pringles';
  if (CLAVES_KELLOGGS.test(k)) return 'kelloggs';
  return null;
}
